// Guided-onboarding rules shared by the in-memory double and the local onboarding
// (contract 1.4.0, `/studio/onboarding`). Pure: failures come back as
// `{ code, status, details, errors }` for the caller to throw.

// What an answer puts in the profile: chosen labels plus own entries, the confirmed
// or written text, nothing when skipped.
export function answerValues(record, subject) {
  const question = record.questions.find(item => item.subject === subject);
  const answer = question && record.answers.find(item => item.question_id === question.id);
  if (!answer || answer.kind === 'skip') return [];
  if (answer.kind !== 'choose') return [answer.text];
  return [...answer.options.map(value => question.options.find(option => option.value === value)?.label).filter(Boolean), ...answer.custom];
}

// The creator profile a completed onboarding creates: only what the creator
// confirmed, corrected or chose.
export const onboardingProfile = (record, memoryEnabled) => ({
  social_accounts: record.account ? [{ platform: record.account.platform, handle: record.account.handle }] : [],
  interests: answerValues(record, 'topics'),
  audience: answerValues(record, 'audience')[0] || null,
  voice_instructions: [...answerValues(record, 'profession'), ...answerValues(record, 'tone')].join('. ') || null,
  locale: record.locale,
  memory_enabled: memoryEnabled,
});

// Questions without `skip` must be answered before completing.
export const missingRequired = record => record.questions
  .filter(question => !question.allowed_responses.includes('skip') && !record.answers.some(answer => answer.question_id === question.id));

const invalid = (field, message) => ({ code: 'validation_error', status: 422, details: {}, errors: { [field]: [message] } });

// `{ answer }` to store (replacing the previous one), or `{ error }`.
export function buildAnswer(record, { question_id, kind, text, options = [], custom = [] }) {
  const question = record.questions.find(item => item.id === question_id);
  if (!question) return { error: invalid('question_id', 'Unknown question.') };
  if (!question.allowed_responses.includes(kind)) return { error: invalid('kind', 'Not allowed for this question.') };
  if (kind === 'answer' && (!text?.trim() || text.length > question.max_characters)) return { error: invalid('text', 'Invalid answer.') };
  const own = custom.map(entry => entry.trim()).filter(Boolean);
  if (kind === 'choose' && (options.some(value => !question.options.some(option => option.value === value)) || (!question.multiple && options.length > 1)
    || (own.length > 0 && !question.allows_custom) || options.length + own.length === 0)) return { error: invalid('options', 'Invalid choice.') };
  return { answer: { question_id, kind, text: kind === 'confirm' ? question.suggested_text : kind === 'answer' ? text.trim() : null,
    options: kind === 'choose' ? options : [], custom: kind === 'choose' ? own : [] } };
}
